import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { CommonActions } from '@react-navigation/native';
import { Keyboard, View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import getSymbolFromCurrency from 'currency-symbol-map';
import { AppView, Spacer, rem } from 'design-system';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import Indicator from 'design-system/Indicator';
import StatusBar from 'design-system/StatusBar';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { SettingsItem, SettingsItemMoneyInput, SettingsItemRightContent, } from 'design-system/SettingsItem';
import { SettingsSection } from 'design-system/SettingsSection';
import Text from 'design-system/Text';
import { KeyboardAvoidingButton } from 'design-system/KeyboardAvoidingButton';
import { IconArrow } from 'design-system/icons/IconArrow';
import { IconBoldArrow } from 'design-system/icons/IconBoldArrow';
import { IconBoldCalendar } from 'design-system/icons/IconBoldCalendar';
import { getFeed } from 'actions/feed';
import { resetError } from 'actions';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import Alert from 'utils/packages/Alert';
import Modal from 'utils/packages/Modal';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import useAppRating from 'hooks/useAppRating';
import createStyleSheets from 'utils/createStyleSheets';
import { muteErrorAndResult, useFetchOld } from 'hooks';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { getOrdinalSuffix } from 'utils/formatting';
import { useRequiredBenefitCallbackWithSpaces } from 'features/premium/hooks/useRequiredBenefitCallback';
import { selectTabsPriority } from 'reducers/selectors/organiseTabs';
import { selectIsGBUser } from 'reducers/selectors';
import { rangeWindowSize } from 'utils/dates';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { FREQUENCY, MONTHLY } from '../utils/frequency';
import { getRangeString } from '../utils/amounts';
import { addSubscription, editSubscription, getSubscriptions, getSubscriptionsById, } from '../actions';
import { makeSelectSubscriptionById, selectIncludeIncomeSubscriptions, } from '../selectors';
import SelectDateRangeModalContent from '../components/modal/SelectDateRangeModalContent';
import SubscriptionFrequencyModalContent from '../components/modal/SubscriptionFrequencyModalContent';
import RowSubscriptionTransaction from '../components/transactions/RowSubscriptionTransaction';
import { isDesktop, isWeb } from '../../../constants';
const getSubscriptionMoney = (subscription) => subscription.nativePrice ?? {
    amount: subscription.amount,
    currency: subscription.currency,
};
const getTransactionMoney = (transaction) => transaction.nativeAmount ?? {
    amount: transaction.amount,
    currency: transaction.currency,
};
const RULES_DONT_MATCH_MESSAGE = 'Unable to create a new subscription from this transaction. Please try again later and if the problem persists contact support.';
const strings = {
    selectDateRange: 'Select the date range you are expecting recurring payment to happen. Please choose at least 3 days to allow for any delays',
    create: {
        title: 'Set matching rules',
        subTitle: 'We’ll use these rules to automatically identify and track this recurring payment',
        cta: 'Continue',
    },
    edit: {
        title: 'Edit matching rules',
        subTitle: 'This rule automatically matches your payments. Editing will update all past and future transactions, but will not affect any manually added transactions',
        cta: 'Save',
    },
    activate: {
        title: 'Review matching rules',
        subTitle: 'We’ll use these rules to automatically identify and track this recurring payment',
        cta: 'Continue',
    },
    createSuccess: {
        title: 'Success',
        subTitle: 'New recurring payment has been created. This counts towards your Committed spending and updates your True balance.',
    },
    activateSuccess: {
        title: 'Success',
        subTitle: 'We’ve updated your recurring payment',
    },
    minimum: 'Minimum amount (Optional)',
    maximum: 'Maximum amount (Optional)',
    placeholder: 'Set value...',
    frequency: 'Frequency',
    period: 'Period',
    choose: 'Choose...',
    anyDay: 'Any day of the month',
    invalidRangeTitle: 'Check your amounts',
    invalidRangeMessage: 'The maximum amount is lower than the minimum amount.',
    rulesDontMatchTitle: 'Rules don’t match',
    rulesDontMatchSubtitle: "This rule doesn't match the details of the original transaction. Please review it and try again",
    okay: 'Okay',
};
const getModeCopy = (isEditing, isActivating) => {
    if (!isEditing) {
        return strings.create;
    }
    return isActivating ? strings.activate : strings.edit;
};
const buildInitialState = (subscription) => {
    const min = subscription?.minAmount != null
        ? Math.floor(subscription.minAmount).toFixed(0)
        : '';
    const max = subscription?.maxAmount != null
        ? Math.ceil(subscription.maxAmount).toFixed(0)
        : '';
    // Persisted "any day" (any 31-day window, wrapped or not) and single day
    // (min === max) are collapsed back to the editor's empty / single-day
    // states so the calendar doesn't render a full or self-overlapping range
    // on re-edit. Save re-derives the canonical values in buildRulesBody.
    const monthFrom = subscription?.minMonthDay ?? null;
    const monthTo = subscription?.maxMonthDay ?? null;
    const isFullMonthRange = monthFrom != null &&
        monthTo != null &&
        rangeWindowSize(monthFrom, monthTo) === 31;
    const isSingleDay = monthFrom != null && monthFrom === monthTo;
    return {
        minimumAmount: min,
        maximumAmount: max,
        frequency: subscription?.paymentFrequency ?? null,
        from: isFullMonthRange ? null : monthFrom,
        to: isFullMonthRange || isSingleDay ? null : monthTo,
    };
};
const AdjustSubscriptionScreen = withAnimatedNavigationHeader(({ navigation, route, onScroll }) => {
    const dispatch = useAppDispatch();
    const colors = useColors();
    const styles = useStyles(styleSet);
    const rateApp = useAppRating();
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle, styles.scrollContent);
    const txn = route.params?.data;
    const editId = route.params?.id;
    const customName = route.params?.customName;
    const isEditing = !!route.params?.edit || !!editId;
    const isActivating = !!route.params?.activate;
    const copy = getModeCopy(isEditing, isActivating);
    useEffect(() => {
        if (editId) {
            dispatch(getSubscriptionsById(editId));
        }
    }, [dispatch, editId]);
    const selectSubscriptionById = useMemo(makeSelectSubscriptionById, []);
    const subscription = useAppSelector((store) => selectSubscriptionById(store, editId));
    const minimumAmountInputRef = useRef(null);
    const maximumAmountInputRef = useRef(null);
    const initialState = useMemo(() => buildInitialState(subscription), [subscription]);
    const [state, setState] = useState(initialState);
    // In edit mode the subscription is resolved from the store, so on a cold
    // load (web refresh) it isn't available for the initial state. Re-seed the
    // editor once it arrives, without clobbering any edits the user has made.
    const didSeedFromSubscription = useRef(!isEditing);
    useEffect(() => {
        if (didSeedFromSubscription.current || !subscription) {
            return;
        }
        didSeedFromSubscription.current = true;
        setState(initialState);
    }, [subscription, initialState]);
    const openCalendar = useCallback(() => {
        const onFinish = ({ start, end }) => {
            setState((s) => ({ ...s, from: start ?? null, to: end ?? null }));
            Modal.hide();
        };
        Keyboard.dismiss();
        Modal.show(<SelectDateRangeModalContent allowIncompleteSelection anyDayWhenFull subtitle={strings.selectDateRange} minRangeDays={3} data={{
                start: state.from || undefined,
                end: state.to || undefined,
            }} onFinish={onFinish}/>, {
            title: isWeb ? 'Select a range' : undefined,
        });
    }, [state.from, state.to]);
    const onOpenFrequency = useCallback(() => {
        const select = (frequency) => {
            setState((s) => ({ ...s, frequency }));
            Modal.hide();
        };
        Keyboard.dismiss();
        Modal.show(<SubscriptionFrequencyModalContent paymentFrequency={state.frequency || undefined} onSelect={select}/>, { increasedTopPadding: true, title: isWeb ? 'Frequency' : undefined });
    }, [state.frequency]);
    const onChangeMinimumAmount = useCallback((_, rawData) => {
        if (rawData !== undefined) {
            setState((s) => ({ ...s, minimumAmount: String(rawData) }));
        }
    }, []);
    const onClearMinimumAmount = useCallback(() => {
        setState((s) => ({ ...s, minimumAmount: '' }));
    }, []);
    const onChangeMaximumAmount = useCallback((_, rawData) => {
        if (rawData !== undefined) {
            setState((s) => ({ ...s, maximumAmount: String(rawData) }));
        }
    }, []);
    const onClearMaximumAmount = useCallback(() => {
        setState((s) => ({ ...s, maximumAmount: '' }));
    }, []);
    const [, , isFetching, fetch] = useFetchOld(muteErrorAndResult);
    const [isCreating, setIsCreating] = useState(false);
    const { priority } = useAppSelector(selectTabsPriority);
    const isGBUser = useAppSelector(selectIsGBUser);
    const includeIncome = useAppSelector(selectIncludeIncomeSubscriptions);
    const buildRulesBody = useCallback(() => {
        const { from, to, minimumAmount, maximumAmount, frequency } = state;
        const body = {};
        if (minimumAmount)
            body.minAmount = minimumAmount;
        else if (initialState.minimumAmount)
            body.minAmount = null;
        if (maximumAmount)
            body.maxAmount = maximumAmount;
        else if (initialState.maximumAmount)
            body.maxAmount = null;
        if (frequency)
            body.userPaymentFrequency = frequency;
        if (frequency === MONTHLY) {
            // "Any day" (no selection) and any 31-day window — including wrapped
            // ones like 2nd–1st — mean the same thing, so all are persisted as
            // the canonical 1–31. A single day is sent as min === max.
            const isFullMonthRange = from != null && to != null && rangeWindowSize(from, to) === 31;
            body.minMonthDay = isFullMonthRange ? 1 : from ?? 1;
            body.maxMonthDay = isFullMonthRange ? 31 : to ?? from ?? 31;
        }
        if (customName)
            body.customName = customName;
        return body;
    }, [state, initialState, customName]);
    const ensureAmountsOrdered = useCallback(() => {
        if (state.minimumAmount &&
            state.maximumAmount &&
            Number(state.maximumAmount) < Number(state.minimumAmount)) {
            Alert.alert(strings.invalidRangeTitle, strings.invalidRangeMessage);
            return false;
        }
        return true;
    }, [state.maximumAmount, state.minimumAmount]);
    const onPressCreate = useRequiredBenefitCallbackWithSpaces(useCallback(async () => {
        if (!txn)
            return;
        Keyboard.dismiss();
        if (!ensureAmountsOrdered())
            return;
        const body = buildRulesBody();
        body.baseTransactionId = txn.id;
        setIsCreating(true);
        const response = await dispatch(addSubscription(body));
        setIsCreating(false);
        if (response?.payload?.subscription) {
            dispatch(getFeed());
            dispatch(getSubscriptions(includeIncome));
            navigation.navigateDeprecated('Success', {
                title: strings.createSuccess.title,
                subTitle: strings.createSuccess.subTitle,
                onContinue: () => {
                    if (route.params?.onSuccess) {
                        navigation.pop(3);
                    }
                    else {
                        navigation.navigateDeprecated(!isGBUser && priority.includes('recurring')
                            ? 'RecurringTab'
                            : 'Subscriptions');
                    }
                    rateApp();
                    return true;
                },
            });
            route.params?.onSuccess?.();
            return;
        }
        const userMessage = response?.apiPayload?.fullResponse?.user_message;
        if (userMessage === RULES_DONT_MATCH_MESSAGE) {
            Modal.showConfirmation({
                title: strings.rulesDontMatchTitle,
                subtitle: (<>
                  <Text TextThin-16 Secondary centered>
                    {strings.rulesDontMatchSubtitle}
                  </Text>
                  <Spacer h={16}/>
                  <View style={styles.rulesCard}>
                    <RowSubscriptionTransaction backgroundLight data={txn}/>
                  </View>
                </>),
                confirmButtonTitle: strings.okay,
            });
        }
        else if (!response?.errorDisplayModal) {
            const onDismiss = () => dispatch(resetError());
            Modal.showError(response?.errorMessage || 'This is awkward, please try again.', onDismiss, response?.error || 'Error', 'Okay', onDismiss);
        }
    }, [
        ensureAmountsOrdered,
        buildRulesBody,
        txn,
        dispatch,
        navigation,
        route.params,
        rateApp,
        isGBUser,
        includeIncome,
        priority,
        styles.rulesCard,
    ]), 'recurringPayments');
    const hasChanges = state.minimumAmount !== initialState.minimumAmount ||
        state.maximumAmount !== initialState.maximumAmount ||
        state.frequency !== initialState.frequency ||
        state.from !== initialState.from ||
        state.to !== initialState.to;
    const goBackToSubscription = useCallback(() => {
        if (!subscription)
            return;
        if (navigation.canGoBack()) {
            navigation.goBack();
        }
        else {
            navigation.dispatch(CommonActions.reset({
                index: 0,
                routes: [
                    { name: 'Subscription', params: { id: subscription.id } },
                ],
            }));
        }
    }, [navigation, subscription]);
    const onPressUpdate = useCallback(async () => {
        if (!subscription) {
            return;
        }
        Keyboard.dismiss();
        if (!ensureAmountsOrdered())
            return;
        if (!isActivating && !hasChanges) {
            goBackToSubscription();
            return;
        }
        const body = buildRulesBody();
        if (isActivating) {
            body.isActive = true;
        }
        const response = await fetch(editSubscription(subscription.id, body));
        if (!response?.subscription) {
            return;
        }
        dispatch(getFeed());
        dispatch(getSubscriptions(includeIncome));
        if (isActivating) {
            if (navigation
                .getState()
                .routes.some((r) => r.name === 'InactivePayments')) {
                navigation.dispatch((state) => {
                    const routes = state.routes.filter((r) => r.name !== 'InactivePayments');
                    return CommonActions.reset({
                        ...state,
                        routes,
                        index: routes.length - 1,
                    });
                });
            }
            navigation.navigateDeprecated('Success', {
                title: strings.activateSuccess.title,
                subTitle: strings.activateSuccess.subTitle,
                onContinue: () => {
                    const inStack = navigation
                        .getState()
                        .routes.some((r) => r.name === 'Subscription' &&
                        r.params?.id ===
                            subscription.id);
                    if (inStack) {
                        navigation.popTo('Subscription', { id: subscription.id });
                    }
                    else {
                        navigation.dispatch(CommonActions.reset({
                            index: 1,
                            routes: [
                                { name: 'Subscriptions' },
                                {
                                    name: 'Subscription',
                                    params: { id: subscription.id },
                                },
                            ],
                        }));
                    }
                    return true;
                },
            });
            return;
        }
        goBackToSubscription();
    }, [
        ensureAmountsOrdered,
        hasChanges,
        goBackToSubscription,
        buildRulesBody,
        isActivating,
        subscription,
        dispatch,
        navigation,
        includeIncome,
    ]);
    if (isEditing && !subscription) {
        return (<AppView withFrame={false}>
            <StatusBar barStyle="dark-content" animated/>
            <Indicator brand/>
          </AppView>);
    }
    const { frequency, from, to } = state;
    const canSubmit = frequency !== null;
    const isFullRange = from != null && to != null && rangeWindowSize(from, to) === 31;
    let periodTitle;
    if (!from || isFullRange) {
        periodTitle = strings.anyDay;
    }
    else if (!to || to === from) {
        periodTitle = `${from}${getOrdinalSuffix(from)} of the month`;
    }
    else {
        periodTitle = `${getRangeString(from, to)} of the month`;
    }
    let currencyCode = '';
    if (txn) {
        currencyCode = getTransactionMoney(txn).currency;
    }
    else if (subscription) {
        currencyCode = getSubscriptionMoney(subscription).currency;
    }
    const currencySymbol = getSymbolFromCurrency(currencyCode);
    return (<AppView withFrame={false}>
          <KeyboardAwareScrollView onScroll={onScroll} scrollEventThrottle={16} contentContainerStyle={contentContainerStyle} keyboardShouldPersistTaps="handled" mode="layout" bottomOffset={HEIGHT_PLUS_PADDING}>
            <ScreenInfoHeader title={isEditing && !isActivating ? undefined : copy.title} subTitle={copy.subTitle}/>
            <SettingsSection noMarginTop>
              <SettingsItem topDescription={strings.frequency} title={frequency ? (FREQUENCY[frequency]) : (<Text Text-16 color={colors.text.placeholder}>
                      {strings.choose}
                    </Text>)} onPress={onOpenFrequency} icon={<IconBoldCalendar gradientKey="apricot"/>}>
                <SettingsItemRightContent>
                  <IconArrow direction="down"/>
                </SettingsItemRightContent>
              </SettingsItem>
              {frequency === MONTHLY ? (<SettingsItem topDescription={strings.period} title={periodTitle} onPress={openCalendar} icon={<IconBoldCalendar gradientKey="darkPurple"/>}>
                  <SettingsItemRightContent>
                    <IconArrow direction="down"/>
                  </SettingsItemRightContent>
                </SettingsItem>) : null}
              <SettingsItemMoneyInput ref={minimumAmountInputRef} description={strings.minimum} icon={<IconBoldArrow direction="left" gradientKey="darkPink"/>} prefix={currencySymbol} maximumFractionalDigits={0} value={state.minimumAmount} useNumbersFont={!!state.minimumAmount} showClearOnZero returnKeyType={isDesktop ? undefined : 'done'} onChangeText={onChangeMinimumAmount} onClearTextInput={onClearMinimumAmount} placeholder={strings.placeholder} maximumIntegerDigits={9}/>
              <SettingsItemMoneyInput ref={maximumAmountInputRef} description={strings.maximum} icon={<IconBoldArrow direction="right" gradientKey="skyBlue"/>} prefix={currencySymbol} maximumFractionalDigits={0} value={state.maximumAmount} useNumbersFont={!!state.maximumAmount} showClearOnZero returnKeyType={isDesktop ? undefined : 'done'} onChangeText={onChangeMaximumAmount} onClearTextInput={onClearMaximumAmount} placeholder={strings.placeholder} maximumIntegerDigits={9}/>
            </SettingsSection>
          </KeyboardAwareScrollView>
          <KeyboardAvoidingButton brand title={copy.cta} isFetching={isEditing ? isFetching : isCreating} disabled={!canSubmit} onPress={isEditing ? onPressUpdate : onPressCreate}/>
        </AppView>);
}, ({ route, colors }) => {
    const isEditing = !!route.params?.edit || !!route.params?.id;
    const isActivating = !!route.params?.activate;
    const copy = getModeCopy(isEditing, isActivating);
    return {
        title: copy.title,
        pageTitle: copy.title,
        hideTitleOnScroll: !isEditing || isActivating,
        headerColorOutputRange: [
            colors.background.dark,
            colors.background.dark,
        ],
    };
});
const styleSet = createStyleSheets((colors) => ({
    scrollContent: {
        flexGrow: 1,
        paddingTop: rem(8),
    },
    rulesCard: {
        borderRadius: rem(16),
        overflow: 'hidden',
        width: '100%',
        backgroundColor: colors.background.dark,
    },
}));
export default AdjustSubscriptionScreen;
